import {cn} from '@sollapay/ui';
import {ROW_REVEAL_CLASS} from '@sollapay/ui/components';

import type {FC, ReactNode} from 'react';

/**
 * The list card's shell, filled by the member card and the guest card. It carries `group/row`, so the
 * row-hover reveals (number chip, call to action) work as in a table row, and `identity` is
 * expected to hold the link stretched over the whole card.
 */
export interface TrustAccountGridCardProps {
  identity: ReactNode;
  /** Revealed on hover or focus, and always shown on touch. */
  menu?: ReactNode;
  balance?: ReactNode;
  meta?: ReactNode;
  footerStart: ReactNode;
  footerEnd: ReactNode;
}

export const TrustAccountGridCard: FC<TrustAccountGridCardProps> = ({
  identity,
  menu,
  balance,
  meta,
  footerStart,
  footerEnd
}) => (
  <article
    data-slot="trust-account-grid-card"
    className={cn(
      'group/row relative flex min-w-0 flex-col gap-3.5 rounded-xl border border-border bg-background-surface p-3.5 transition-[border-color,box-shadow] hover:border-brand-accent/40 hover:shadow-card-hover has-[:focus-visible]:border-brand-accent/40',
      !balance && 'pt-4.5'
    )}
  >
    {balance}
    {/* Takes the spare height, so the rows below sit at the same height in every card of a grid row. */}
    <div data-slot="card-identity" className="flex flex-1 items-start gap-2 px-1">
      <div className="min-w-0 flex-1">{identity}</div>
      {menu && (
        <div
          data-slot="card-menu"
          className={cn(
            'relative z-10 -my-1 shrink-0',
            ROW_REVEAL_CLASS,
            'has-[[data-popup-open]]:opacity-100'
          )}
        >
          {menu}
        </div>
      )}
    </div>
    {meta && <div className="px-1">{meta}</div>}
    <div
      data-slot="card-footer"
      className="flex min-h-10 items-center justify-between gap-3 border-t border-border-subtle px-1 pt-3"
    >
      <div className="min-w-0">{footerStart}</div>
      <div className="min-w-0">{footerEnd}</div>
    </div>
  </article>
);
